import React, {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from "react";
import DeletePopUp from "../DeletePopUp";
import { Pencil } from "lucide-react";
import { ICategory, IDropdownOption } from "../../exports/interfaces";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { fetchDropdownCategories } from "../../api/AdminApi";
import { useLoader } from "../../context/LoaderContext";
import { usePermission } from "../../hooks/usePermission";

export const getStatusClass = (status: string) => {
  switch (status) {
    case "Active":
      return "pill-success";
    case "Inactive":
      return "pill-danger";
    case "Deactivated":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

const Dropdown = ({
  categories,
  setCategories,
}: {
  categories: ICategory[];
  setCategories: Dispatch<SetStateAction<ICategory[]>>;
}) => {
  const { setLoading } = useLoader();
  const { canCreate, canEdit, canDelete } = usePermission();
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(
    null,
  );
  const [tableRows, setTableRows] = useState<IDropdownOption[]>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [newValue, setNewValue] = useState<string>("");
  const [newLabel, setNewLabel] = useState<string>("");
  const [newOrder, setNewOrder] = useState<number>(0);
  const [newStatus, setNewStatus] = useState<string>("Active");

  // Track if we've initialized selectedCategory
  const initialized = useRef(false);

  const handleCategorySelect = (category: ICategory) => {
    setSelectedCategory(category);
    setTableRows(category.dropdowns);
    const maxOrder =
      category.dropdowns && category.dropdowns.length
        ? Math.max(...category.dropdowns.map((d) => Number(d.order || 0)))
        : 0;
    setNewOrder(maxOrder + 1);
  };

  useEffect(() => {
    if (!initialized.current && categories.length > 0) {
      initialized.current = true;
      const first = categories[0];
      setSelectedCategory(first);
      setTableRows(first.dropdowns);
      const maxOrder =
        first.dropdowns && first.dropdowns.length
          ? Math.max(...first.dropdowns.map((d) => Number(d.order || 0)))
          : 0;
      setNewOrder(maxOrder + 1);
    }
  }, [categories]);

  const handleRowChange = (
    id: number,
    field: "label" | "value",
    value: string,
  ) => {
    setTableRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const handleClose = () => {
    setShowDelete(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(
        `/options/dropdowns/${deleteId}`,
      );
      if (response.status === 200 || response.status === 201) {
        const categoryData = await fetchDropdownCategories();
        setCategories(categoryData);
        const updatedCategory =
          categoryData.find((c) => c.id === selectedCategory?.id) || null;
        setSelectedCategory(updatedCategory);
        setTableRows(updatedCategory ? updatedCategory.dropdowns : []);
        setShowDelete(false);
        setDeleteId(null);
        setTimeout(() => {
          setLoading(false);
          dynamicSuccess("Dropdown option deleted successfully!");
        }, 1000);
      }
    } catch (error: any) {
      setTimeout(() => {
        dynamicFailed(`${error.message}`);
        setLoading(false);
      }, 1000);
    }
  };

  const handleSaveEdit = async (rowId: number) => {
    const editedRow = tableRows.find((r) => r.id === rowId);
    if (!editedRow) return;

    const payload = {
      label: editedRow.label,
      value: editedRow.value,
      order: editedRow.order,
      status: editedRow.status,
    };

    try {
      setLoading(true);
      const response = await axiosClient.put(
        `/options/dropdowns/${rowId}`,
        payload,
      );

      if (response.status === 200 || response.status === 201) {
        const categoryData = await fetchDropdownCategories();
        setCategories(categoryData);
        const updatedCategory =
          categoryData.find((c) => c.id === selectedCategory?.id) || null;
        setSelectedCategory(updatedCategory);
        setTableRows(updatedCategory ? updatedCategory.dropdowns : []);
        setEditingId(null);

        dynamicSuccess("Dropdown option updated successfully!");
      } else {
        dynamicFailed("Failed to update option");
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCategory) {
      dynamicFailed("Please select a category first!");
      return;
    }

    const payload = {
      label: newLabel,
      value: newValue,
      order: newOrder,
      status: newStatus,
      categoryId: selectedCategory.id,
    };

    try {
      setLoading(true);
      const response = await axiosClient.post("/options/dropdowns", payload);

      if (response.status === 200 || response.status === 201) {
        const categoryData = await fetchDropdownCategories();
        setCategories(categoryData);
        const updatedCategory =
          categoryData.find((c) => c.id === selectedCategory.id) || null;
        setSelectedCategory(updatedCategory);
        setTableRows(updatedCategory ? updatedCategory.dropdowns : []);
        setNewLabel("");
        setNewValue("");
        setNewOrder(0);
        setNewStatus("Active");
        setShowForm(false);

        dynamicSuccess("Dropdown option added successfully!");
      } else {
        dynamicFailed("Failed to add option");
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  return (
    <>
      <div className="row g-3">
        {/* Left Sidebar */}
        <div className="col-lg-3 col-md-5">
          <div className="dashboard-card h-100 p-0 overflow-hidden">
            <h5 className="primary-color p-2 border-bottom ">Categories</h5>

            <div className="list-group list-group-flush custom-list-group">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`list-group-item list-group-item-action border-0 text-start ${
                    selectedCategory?.id === category.id
                      ? "active-category"
                      : ""
                  }`}
                  onClick={() => handleCategorySelect(category)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="col-lg-9 col-md-7">
          <div className="dashboard-card">
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
              <div>
                <h5 className="primary-color">{selectedCategory?.name}</h5>
                <small>Manage available options for this dropdown field</small>
              </div>

              {canCreate("Admin Control") && (
                <button
                  className="btn btn-badge-primary px-3"
                  onClick={() => {
                    if (selectedCategory) {
                      const maxOrder =
                        selectedCategory.dropdowns &&
                        selectedCategory.dropdowns.length
                          ? Math.max(
                              ...selectedCategory.dropdowns.map((d) =>
                                Number(d.order || 0),
                              ),
                            )
                          : 0;
                      setNewOrder(maxOrder + 1);
                    }
                    setShowForm((prev) => !prev);
                  }}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Add Option
                </button>
              )}
            </div>
            <div
              className={`option-form-wrapper dashboard-card ${showForm ? "open" : "p-0"}`}
            >
              <div className="option-form">
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Value (Slug)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g Agriculture"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                    />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Label (Display)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g Agriculture"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                    />
                  </div>

                  <div className="col-md-12 col-lg-3">
                    <label className="form-label">Sort Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newOrder}
                      onChange={(e) => setNewOrder(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    className="btn btn-badge-border px-3"
                    onClick={() => {
                      setShowForm(false);
                      setNewLabel("");
                      setNewValue("");
                      setNewOrder(0);
                      setNewStatus("Active");
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleSubmit}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="table-responsive admin-table table-checkbox">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Value</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th className="sticky-action">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {editingId === row.id ? (
                          <input
                            type="text"
                            className="form-control"
                            value={row.label}
                            onChange={(e) =>
                              handleRowChange(row.id, "label", e.target.value)
                            }
                          />
                        ) : (
                          row.label
                        )}
                      </td>

                      <td>
                        {editingId === row.id ? (
                          <input
                            type="text"
                            className="form-control"
                            value={row.value}
                            onChange={(e) =>
                              handleRowChange(row.id, "value", e.target.value)
                            }
                          />
                        ) : (
                          row.value
                        )}
                      </td>

                      <td>{row.order}</td>

                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 ${getStatusClass(
                            row.status,
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>

                      <td className="sticky-action">
                        <div className="d-flex gap-3 align-items-center">
                          {editingId === row.id ? (
                            <>
                              <i
                                className={`fa-solid fa-floppy-disk text-success ${
                                  canEdit("Admin Control") ? "cursor-pointer" : "opacity-50"
                                }`}
                                title={
                                  canEdit("Admin Control") ? "Save" : "No permission"
                                }
                                style={{
                                  pointerEvents: canEdit("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canEdit("Admin Control")) return;
                                  handleSaveEdit(row.id);
                                }}
                              ></i>

                              <i
                                className={`fa-solid fa-trash text-danger ${
                                  canDelete("Admin Control") ? "cursor-pointer" : "opacity-50"
                                }`}
                                title={
                                  canDelete("Admin Control") ? "Delete" : "No permission"
                                }
                                style={{
                                  pointerEvents: canDelete("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canDelete("Admin Control")) return;
                                  setDeleteId(row?.id || 0);
                                  setShowDelete(true);
                                }}
                              ></i>
                            </>
                          ) : (
                            <>
                              <i
                                className={`cursor-pointer ${
                                  canEdit("Admin Control") ? "" : "opacity-50"
                                }`}
                                title={
                                  canEdit("Admin Control") ? "Edit" : "No permission"
                                }
                                style={{
                                  pointerEvents: canEdit("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canEdit("Admin Control")) return;
                                  setEditingId(row.id);
                                }}
                              >
                                <Pencil size={20} />
                              </i>

                              <i
                                className={`fa-solid fa-trash text-danger ${
                                  canDelete("Admin Control") ? "cursor-pointer" : "opacity-50"
                                }`}
                                title={
                                  canDelete("Admin Control") ? "Delete" : "No permission"
                                }
                                style={{
                                  pointerEvents: canDelete("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canDelete("Admin Control")) return;
                                  setDeleteId(row?.id || 0);
                                  setShowDelete(true);
                                }}
                              ></i>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default Dropdown;
